import * as React from 'react';
import { AlertTriangle, FileText, Info, Upload } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { Spinner } from '@/components/ui/spinner';
import { ErrorMessage } from '@/components/ui/error-message';
import { StatusChip } from '@/components/ui/status-chip';
import {
  Callout,
  Step,
  Steps,
  WorkspaceButton,
  linkishClass,
  useToast,
  workspaceInputClass,
} from '@/components/ui/workspace';
import { discoveryKeys } from '@/lib/api/discovery';
import {
  attachTranscript,
  isReading,
  rereadTranscript,
  useReviewQueueQuery,
  useTranscriptQuery,
  useTranscriptsQuery,
  type RunStatus,
  type TranscriptDetail,
  type TranscriptVersion,
} from '@/lib/api/discovery-review';
import { useDiscoveryWrite, type InterviewDetail } from '@/lib/api/discovery-sessions';
import { uploadFileChunked } from '@/lib/api/documents';
import { useEngagementQuery } from '@/lib/api/engagements';
import { cn } from '@/lib/utils/cn';
import { plural } from '../discovery-logic';
import { errorText } from '../sessions/errors';
import { QuestionnaireStep } from '../sessions/questionnaire-step';
import { longDay } from '../sessions/sessions-logic';
import { ReviewQueue } from './review-queue';
import { TRANSCRIPT_ACCEPT, isConfident, readingPercent, transcriptFile } from './review-logic';
import { RoomStep, roomDone, roomSummary } from './room-step';

/**
 * One interview after the session (design note section 3): the file line
 * (version, Upload corrected version, Full transcript), then two steps, only
 * the current one open: "Who was in the room?" (match Speaker 1..N to
 * people, the interviewer marked) and "Review what was said" (the review
 * queue, scoped to this interview). A held interview with no transcript yet
 * shows the drop zone; a reading shows its progress; a failed reading can be
 * read again. A corrected transcript is a new version (Decision 54.18): its
 * note sits on the file line, and an earlier version with items still
 * waiting on its speakers can be opened in the room.
 */
export function AfterSession({
  engagementId,
  d,
  canExtend,
  interviewName,
}: {
  engagementId: string;
  d: InterviewDetail;
  canExtend: boolean;
  interviewName?: (iv: { id: string; code: string; title: string }) => string;
}) {
  const iid = d.interview.id;
  const list = useTranscriptsQuery(engagementId, iid);
  const queue = useReviewQueueQuery(engagementId);
  const latest = list.data?.transcripts[0] ?? null;
  const [full, setFull] = React.useState(false);
  const [open, setOpen] = React.useState<Partial<Record<'room' | 'review' | 'quest', boolean>>>({});
  // The version in the room: the latest, unless an earlier one with items
  // still waiting on its speakers was opened.
  const [roomId, setRoomId] = React.useState<string | null>(null);
  const latestId = latest?.transcript_id ?? null;
  React.useEffect(() => setRoomId(null), [latestId]);
  const inRoom =
    (roomId && list.data?.transcripts.find((v) => v.transcript_id === roomId)) || latest;
  const detail = useTranscriptQuery(engagementId, iid, inRoom?.transcript_id ?? null, full);
  const upload = useTranscriptUpload(engagementId, d, latest);
  useReadyAlert(engagementId, d, detail.data);

  const qn = d.questionnaire;
  const isOpen = (k: 'room' | 'review' | 'quest', def: boolean) => open[k] ?? def;
  const toggle = (k: 'room' | 'review' | 'quest', def: boolean) =>
    setOpen((o) => ({ ...o, [k]: !isOpen(k, def) }));
  // The questionnaire is its own step, whatever the transcript's state.
  const questStep = (n: number) =>
    qn && (d.interview.status !== 'cancelled' || qn.answered > 0) ? (
      <Step
        n={n}
        title={`Questionnaire · ${qn.name}`}
        state={qn.answered >= qn.total ? 'done' : 'todo'}
        summary={`${qn.answered} of ${qn.total} answered`}
        open={isOpen('quest', false)}
        onToggle={() => toggle('quest', false)}
      >
        <QuestionnaireStep engagementId={engagementId} d={d} canExtend={canExtend} />
      </Step>
    ) : null;
  const withQuestionnaire = (content: React.ReactNode) => (
    <>
      {content}
      {qn ? <Steps className="mt-4">{questStep(1)}</Steps> : null}
    </>
  );

  if (list.error) return withQuestionnaire(<ErrorMessage error={list.error} />);
  if (!list.data) return withQuestionnaire(<Loading text="Loading the transcript…" />);
  if (upload.busy)
    return (
      <>
        {upload.input}
        {upload.progress}
      </>
    );
  if (!latest) {
    return withQuestionnaire(
      <>
        {upload.input}
        <DropZone held onPick={upload.pick} />
        <NotesEntry onSave={upload.saveNotes} saving={upload.savingNotes} />
      </>,
    );
  }

  const onLatest = !inRoom || inRoom.transcript_id === latest.transcript_id;
  const run = onLatest ? (detail.data?.run ?? latest.run) : latest.run;
  const name = fileName(latest, d.interview.code);
  if (isReading(run)) {
    const pct = readingPercent(run) ?? d.transcript.progress ?? 0;
    return withQuestionnaire(
      <div className="rounded-card border border-ink-100 bg-paper-0 px-4 py-3.5">
        <div className="font-medium text-ink-900">Caliber is reading {name}</div>
        <Bar pct={pct} label="Reading the transcript" />
        <div className="text-meta text-ink-500">
          Reading {pct}%. Next: who was in the room, then the review. You get an alert when it is
          done.
        </div>
      </div>,
    );
  }
  const fileLine = (
    <FileLine
      name={name}
      version={latest}
      full={full}
      onToggleFull={() => setFull((v) => !v)}
      onCorrect={upload.pick}
    />
  );
  if (run?.status === 'failed') {
    return withQuestionnaire(
      <>
        {upload.input}
        {fileLine}
        <ReadAgain
          engagementId={engagementId}
          d={d}
          transcriptId={latest.transcript_id}
          message={run.error_message}
        />
      </>,
    );
  }
  if (detail.error) return withQuestionnaire(<ErrorMessage error={detail.error} />);
  if (!detail.data) return withQuestionnaire(<Loading text="Loading the transcript…" />);
  const t = detail.data;
  const allDropped =
    onLatest &&
    run?.status === 'ready_for_review' &&
    (run.proposal_count ?? 0) === 0 &&
    (run.dropped_count ?? 0) > 0;

  // What waits for review from this interview, from the queue.
  const mine = (queue.data?.items ?? []).filter((i) => i.interview?.id === iid);
  const ready = mine.filter((i) => i.hold === null);
  const held = mine.length - ready.length;
  const done = roomDone(t);
  const earlier = list.data.transcripts.filter(
    (v) => v.transcript_id !== t.transcript.transcript_id && v.proposed_count > 0,
  );
  const reviewState = ready.length > 0 ? 'current' : done ? 'done' : 'locked';
  const confirmed = t.confirmed.filter((c) => c.status === 'confirmed').length;

  return (
    <>
      {upload.input}
      {fileLine}
      <VersionNote t={t} latestVersionNo={latest.version_no} onBack={() => setRoomId(null)} />
      {allDropped ? (
        <ReadAgain
          engagementId={engagementId}
          d={d}
          transcriptId={latest.transcript_id}
          lead={`Caliber proposed ${plural(run.dropped_count ?? 0, 'passage')} but could not match ${
            (run.dropped_count ?? 0) === 1 ? 'it' : 'them'
          } to the transcript, so nothing was kept.`}
          message={null}
        />
      ) : null}
      <Steps>
        <Step
          n={1}
          title="Who was in the room?"
          state={done ? 'done' : 'current'}
          summary={
            done
              ? roomSummary(t)
              : `${plural(t.speakers.length, 'speaker')}${
                  t.header_attendees?.length
                    ? `, ${plural(t.header_attendees.length, 'attendee')} in the file`
                    : ''
                }`
          }
          open={isOpen('room', !done)}
          onToggle={() => toggle('room', !done)}
        >
          {earlier.map((v) => (
            <Callout
              key={v.transcript_id}
              tone="tip"
              icon={Info}
              className="mb-3 mt-0"
              actions={
                <WorkspaceButton onClick={() => setRoomId(v.transcript_id)}>
                  Open version {v.version_no}
                </WorkspaceButton>
              }
            >
              Version {v.version_no} still has {plural(v.proposed_count, 'item')} to decide.
            </Callout>
          ))}
          <RoomStep
            engagementId={engagementId}
            d={d}
            t={t}
            onSaved={() => setOpen({ room: false, review: true })}
          />
        </Step>
        <Step
          n={2}
          title="Review what was said"
          state={reviewState}
          summary={
            ready.length > 0
              ? `${ready.length} waiting · ${ready.filter(isConfident).length} confident${
                  held ? ` · ${held} wait on the speakers` : ''
                }`
              : held > 0
                ? `${plural(held, 'item waits', 'items wait')} on the speakers`
                : `Reviewed · ${plural(confirmed, 'item')} confirmed`
          }
          open={isOpen('review', done && ready.length > 0)}
          onToggle={() => toggle('review', done && ready.length > 0)}
        >
          {reviewState === 'locked' ? (
            <div className="text-meta text-ink-500">Opens once the speakers are matched.</div>
          ) : (
            <ReviewQueue
              engagementId={engagementId}
              scope={{ kind: 'interview', id: iid }}
              embedded
              hideWho={d.people.length === 1}
              interviewName={interviewName}
            />
          )}
          {confirmed > 0 ? (
            <div className="mt-3 text-meta text-ink-500">
              {plural(confirmed, 'item')} confirmed from this transcript, in Findings by area.
            </div>
          ) : null}
        </Step>
        {questStep(3)}
      </Steps>
      {full ? <FullTranscript t={t} /> : null}
    </>
  );
}

/** Uploading after the session: the drop zone of step 3 before, or of a held interview. */
export function DropZone({ held, onPick }: { held: boolean; onPick: () => void }) {
  return (
    <div className="flex items-center gap-3.5 rounded-card border-[1.5px] border-dashed border-ink-200 px-4 py-3.5 text-ink-500">
      <Upload className="h-[18px] w-[18px] flex-none" aria-hidden="true" />
      <div className="min-w-0 flex-1">
        <div className="font-medium text-ink-900">
          {held ? 'Upload the transcript' : 'Drop the transcript here after the session'}
        </div>
        <div className="text-meta">
          Word, PDF, text or captions (.vtt).{' '}
          {held
            ? 'Next you say who was in the room.'
            : 'Uploading it marks the interview held; next you say who was in the room.'}
        </div>
      </div>
      <WorkspaceButton variant={held ? 'primary' : 'secondary'} size="md" onClick={onPick}>
        Upload
      </WorkspaceButton>
    </div>
  );
}

function Loading({ text }: { text: string }) {
  return (
    <div className="flex items-center gap-2 text-meta text-ink-500">
      <Spinner /> {text}
    </div>
  );
}

function Bar({ pct, label }: { pct: number; label: string }) {
  return (
    <div
      role="progressbar"
      aria-label={label}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={pct}
      className="my-2 h-1.5 max-w-[420px] overflow-hidden rounded-full bg-ink-100"
    >
      <i className="block h-full rounded-full bg-accent-amber-600" style={{ width: `${pct}%` }} />
    </div>
  );
}

const fileName = (v: { document_title: string | null; kind: string }, code: string) =>
  v.document_title ?? `${code} ${v.kind === 'notes' ? 'notes' : 'transcript'}`;

/** The transcript file, its version, "Upload corrected version" and "Full transcript". */
function FileLine({
  name,
  version,
  full,
  onToggleFull,
  onCorrect,
}: {
  name: string;
  version: TranscriptVersion;
  full: boolean;
  onToggleFull: () => void;
  onCorrect: () => void;
}) {
  return (
    <div className="mb-3 flex flex-wrap items-center gap-1.5 text-meta text-ink-500">
      <FileText className="h-3.5 w-3.5" aria-hidden="true" />
      <span>{name}</span>
      <StatusChip tone="neutral" size="sm">
        Version {version.version_no}
      </StatusChip>
      {version.version_no > 1 ? <span>uploaded {longDay(version.created_at)}</span> : null}
      <span className="flex-1" />
      <button type="button" className={linkishClass()} onClick={onCorrect}>
        <Upload className="h-3 w-3" aria-hidden="true" /> Upload corrected version
      </button>
      <button type="button" className={linkishClass()} aria-expanded={full} onClick={onToggleFull}>
        {full ? 'Hide the full transcript' : 'Full transcript'}
      </button>
    </div>
  );
}

/** A corrected version says what was read; an earlier version in the room says so. */
function VersionNote({
  t,
  latestVersionNo,
  onBack,
}: {
  t: TranscriptDetail;
  latestVersionNo: number;
  onBack: () => void;
}) {
  const v = t.transcript.version_no;
  if (v !== latestVersionNo) {
    return (
      <Callout
        tone="tip"
        icon={Info}
        className="mb-3 mt-0"
        actions={
          <WorkspaceButton onClick={onBack}>Back to version {latestVersionNo}</WorkspaceButton>
        }
      >
        You are matching the speakers of version {v}. Version {latestVersionNo} is the latest.
      </Callout>
    );
  }
  if (v === 1) return null;
  return (
    <p className="-mt-1 mb-3 text-meta text-ink-500">
      Version {v} replaces version {v - 1} for reading; evidence already confirmed keeps citing the
      version it came from.{' '}
      {t.transcript.changed_line_count === 0
        ? 'No line changed (timings, case and punctuation are ignored), so nothing new was read.'
        : `Caliber read only the ${plural(t.transcript.changed_line_count, 'line')} that changed.`}
    </p>
  );
}

function ReadAgain({
  engagementId,
  d,
  transcriptId,
  message,
  lead = 'The reading did not finish, so nothing from it was kept.',
}: {
  engagementId: string;
  d: InterviewDetail;
  transcriptId: string;
  message: string | null;
  /** What happened, before the error message. */
  lead?: string;
}) {
  const { toast } = useToast();
  const reread = useDiscoveryWrite(engagementId, () =>
    rereadTranscript(engagementId, d.interview.id, transcriptId),
  );
  return (
    <Callout
      tone="warn"
      icon={AlertTriangle}
      className="mb-3"
      actions={
        <WorkspaceButton
          disabled={reread.isPending}
          onClick={() =>
            reread.mutate(undefined, {
              onSuccess: () => toast({ message: `Reading ${d.interview.code} again.` }),
              onError: (e) => toast({ message: errorText(e), tone: 'critical' }),
            })
          }
        >
          Read it again
        </WorkspaceButton>
      }
    >
      {lead}
      {message ? ` ${message}` : ''}
    </Callout>
  );
}

/** "Type the notes": interview notes are cited by paragraph (Decision 54.17). */
export function NotesEntry({
  onSave,
  saving,
}: {
  onSave: (text: string) => void;
  saving: boolean;
}) {
  const [text, setText] = React.useState<string | null>(null);
  if (text === null) {
    return (
      <button type="button" className={cn(linkishClass(), 'mt-2.5')} onClick={() => setText('')}>
        No recording? Type the notes
      </button>
    );
  }
  return (
    <div className="mt-3">
      <textarea
        aria-label="Interview notes"
        rows={6}
        className={cn(workspaceInputClass, 'h-auto py-2')}
        placeholder="One paragraph per point. Each paragraph can be cited."
        value={text}
        onChange={(e) => setText(e.target.value)}
      />
      <div className="mt-2 flex items-center gap-2">
        <WorkspaceButton
          variant="primary"
          disabled={!text.trim() || saving}
          onClick={() => onSave(text.trim())}
        >
          Save notes
        </WorkspaceButton>
        <button type="button" className={linkishClass()} onClick={() => setText(null)}>
          Cancel
        </button>
      </div>
    </div>
  );
}

function FullTranscript({ t }: { t: TranscriptDetail }) {
  const interviewer = new Set(t.speakers.filter((s) => s.is_interviewer).map((s) => s.id));
  if (!t.lines) return <div className="mt-3 text-meta text-ink-500">Loading the transcript…</div>;
  return (
    <div
      aria-label="Full transcript"
      className="mt-3 max-h-[360px] overflow-y-auto rounded-card border border-ink-100 bg-paper-50 p-2"
    >
      {t.lines.map((l) => (
        <div
          key={l.id}
          className="grid grid-cols-[64px_104px_1fr] gap-3 rounded-chip px-2.5 py-2 text-body text-ink-700"
        >
          <span className="font-mono text-meta leading-[22px] text-ink-500">{l.at}</span>
          <span
            className={cn(
              'text-meta leading-[22px]',
              l.speaker_id && interviewer.has(l.speaker_id)
                ? 'font-medium text-ink-500'
                : 'font-semibold text-ink-900',
            )}
          >
            {l.speaker_label}
          </span>
          <span>{l.text}</span>
        </div>
      ))}
    </div>
  );
}

/**
 * Uploading a transcript (first or corrected version): the E02 chunked
 * upload with the transcript purpose, then the attach that starts reading.
 */
export function useTranscriptUpload(
  engagementId: string,
  d: InterviewDetail,
  latest: TranscriptVersion | null,
) {
  const { toast } = useToast();
  const eng = useEngagementQuery(engagementId);
  const ref = React.useRef<HTMLInputElement>(null);
  const [sending, setSending] = React.useState<{ name: string; pct: number } | null>(null);
  const attach = useDiscoveryWrite(
    engagementId,
    (body: { document_id: string } | { notes_text: string }) =>
      attachTranscript(engagementId, d.interview.id, body),
  );
  const fail = (e: unknown) => toast({ message: errorText(e), tone: 'critical' });
  const attached = (name: string) => (r: { version_no: number; changed_line_count?: number }) =>
    toast({ message: uploadedText(name, r) });

  const onFile = async (file: File) => {
    const projectId = eng.data?.project_id;
    if (!projectId) {
      toast({ message: 'The engagement is still loading. Try again in a moment.' });
      return;
    }
    setSending({ name: file.name, pct: 0 });
    try {
      const up = await uploadFileChunked(transcriptFile(file), {
        projectId,
        title: file.name,
        // An interview transcript is the client's own material (E19 §1);
        // base formation and the contract readers skip transcripts anyway.
        sourceParty: 'client',
        purpose: 'discovery_transcript',
        onProgress: ({ sentBytes, totalBytes }) =>
          setSending({
            name: file.name,
            pct: totalBytes ? Math.round((sentBytes / totalBytes) * 100) : 100,
          }),
      });
      await attach.mutateAsync({ document_id: up.document_id }).then(attached(file.name));
    } catch (e) {
      fail(e);
    } finally {
      setSending(null);
    }
  };

  return {
    busy: sending !== null,
    pick: () => ref.current?.click(),
    savingNotes: attach.isPending,
    saveNotes: (text: string) =>
      attach.mutate(
        { notes_text: text },
        { onSuccess: attached(`${d.interview.code} notes`), onError: fail },
      ),
    input: (
      <input
        ref={ref}
        type="file"
        hidden
        aria-label={latest ? 'Corrected transcript file' : 'Transcript file'}
        accept={TRANSCRIPT_ACCEPT}
        onChange={(e) => {
          const file = e.target.files?.[0];
          e.target.value = '';
          if (file) void onFile(file);
        }}
      />
    ),
    progress: sending ? (
      <>
        <div className="font-medium text-ink-900">Uploading {sending.name}</div>
        <Bar pct={sending.pct} label="Uploading" />
        <div className="text-meta text-ink-500">{sending.pct}% uploaded.</div>
      </>
    ) : null,
  };
}

/**
 * The ready alert: when a reading finishes while the interview is open, say
 * so and read the list and the interview again so their chips move on.
 */
function useReadyAlert(engagementId: string, d: InterviewDetail, t: TranscriptDetail | undefined) {
  const { toast } = useToast();
  const qc = useQueryClient();
  const was = React.useRef<string | null>(null);
  const status = t?.run?.status ?? null;
  React.useEffect(() => {
    const before = was.current;
    was.current = status;
    if (!before || !t || !isReading({ status: before as RunStatus }) || isReading(t.run)) return;
    void qc.invalidateQueries({ queryKey: discoveryKeys.all(engagementId) });
    if (status === 'ready_for_review') {
      toast({
        message:
          t.proposals.length > 0
            ? `${d.interview.code} ${d.interview.title} is read: say who was in the room, then review ${plural(t.proposals.length, 'item')}.`
            : `${d.interview.code} ${d.interview.title} is read: no new evidence was found.`,
      });
    } else if (status === 'failed') {
      toast({ message: `${d.interview.code}: the reading did not finish.`, tone: 'critical' });
    }
  }, [status, t, d.interview.code, d.interview.title, engagementId, qc, toast]);
}

/** The toast after a transcript is attached, first or corrected version. */
export function uploadedText(
  name: string,
  r: { version_no: number; changed_line_count?: number },
): string {
  if (r.version_no === 1) {
    return `${name} uploaded. Caliber is reading it; next you say who was in the room.`;
  }
  const before = `${name} uploaded as version ${r.version_no}. Evidence already confirmed keeps citing version ${r.version_no - 1}`;
  if (r.changed_line_count === 0) {
    return `${before}. Nothing changed from version ${r.version_no - 1} (timings, case and punctuation are ignored), so there is nothing new to read.`;
  }
  return r.changed_line_count === undefined
    ? `${before}; Caliber reads only the passages that changed.`
    : `${before}; Caliber reads only the ${plural(r.changed_line_count, 'line')} that changed.`;
}
